import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import DialogComponent from '../../component/dialog/dialog.component';
import i18n from '../../i18n';
import type { MenuStoreMenuListItem } from '../../models/menu-store.entity';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as AddIcon } from '../../static/images/svg/mingcute_add-fill.svg';
import { ReactComponent as AutoStoriesIcon } from '../../static/images/svg/auto_stories.svg';
import { ReactComponent as MenuBookRoundedIcon } from '../../static/images/svg/material-symbols_menu-book-2-rounded.svg';
import defaultMenuImage from '../../static/images/default-menu.png';
import { MenuStoreVirtuosoPagedGrid } from '../menu-store/menu-store-virtuoso-paged-grid.component';
import { formatMenuStoreDate, resolveMenuStoreCoverImage } from '../menu-store/menu-store.helpers';
import './menu-library-share-store-target-dialog.style.scss';

export type MenuLibraryShareStoreTargetDialogProps = {
    /** 是否打开 */
    open: boolean;
    /** 关闭（含右上角） */
    onClose: () => void;
    /** 当前分享的菜单展示名（可含 ` - DD/MM/YYYY` 更新时间后缀） */
    menuName: string;
    /** 拉取「我的菜单」中 */
    loading: boolean;
    /** 提交新建/覆盖中 */
    submitting: boolean;
    /** 可归为「我上传」的商店菜单项（已过滤 delisted/rejected 等） */
    uploadedMenus: MenuStoreMenuListItem[];
    /** 触底拉取下一页 uploaded 列表时为 true */
    loadingMore: boolean;
    /** 触底或可视不足时请求追加一页 */
    onLoadMore: () => void;
    /** `new` 或待覆盖项 `_id` */
    selection: 'new' | string;
    /** 切换选中项 */
    onSelectionChange: (value: 'new' | string) => void;
    /** 主按钮提交 */
    onSubmit: () => void;
};

/**
 * 分享弹窗虚拟栅格单行：首项为「新增菜单」，其后为已上传商店菜单项。
 */
type ShareStoreTargetGridRow =
    /** 占位首格，渲染 `NewMenuCard` */
    | { kind: 'new' }
    /** 已上传项，渲染 `ExistingMenuCard` */
    | { kind: 'menu'; item: MenuStoreMenuListItem };

type StoreLikeCardShellProps = {
    /** 是否选中（描边高亮） */
    selected: boolean;
    /** 点击选中 */
    onSelect: () => void;
    /** 子元素：置于叠层之上 */
    children: React.ReactNode;
};

/**
 * 已上传项：底图 + 遮罩 + 白字（与菜单商店列表同源，尺寸按稿 168×224）
 */
function StoreLikeCardShell(props: StoreLikeCardShellProps) {
    const { selected, onSelect, children } = props;

    const handleKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            onSelect();
        }
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            className={[
                'menu-library-share-store-target-dialog__store-card',
                selected ? 'menu-library-share-store-target-dialog__store-card--selected' : '',
            ]
                .filter(Boolean)
                .join(' ')}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            {children}
        </Box>
    );
}

type ExistingMenuCardProps = {
    /** 商店菜单项 */
    item: MenuStoreMenuListItem;
    /** 是否选中 */
    selected: boolean;
    /** 选中回调 */
    onSelect: () => void;
};

/**
 * 已上传菜单卡片：封面与默认图逻辑同菜单商店列表
 */
function ExistingMenuCard(props: ExistingMenuCardProps) {
    const { item, selected, onSelect } = props;
    const coverImage = resolveMenuStoreCoverImage(item);
    const [imgSrc, setImgSrc] = useState(() => coverImage || defaultMenuImage);

    useEffect(() => {
        setImgSrc(coverImage || defaultMenuImage);
    }, [coverImage]);

    const dateLabel = formatMenuStoreDate(item.updated_at ?? item.created_at);

    return (
        <StoreLikeCardShell selected={selected} onSelect={onSelect}>
            <img
                className="menu-library-share-store-target-dialog__store-card-bg"
                src={imgSrc}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                onError={() => {
                    setImgSrc(defaultMenuImage);
                }}
            />
            <Box className="menu-library-share-store-target-dialog__store-card-scrim" aria-hidden />
            <Box className="menu-library-share-store-target-dialog__store-card-inner">
                <AutoStoriesIcon className="menu-library-share-store-target-dialog__store-card-icon" aria-hidden />
                <Typography component="p" className="menu-library-share-store-target-dialog__store-card-title" title={item.name}>
                    {item.name}
                </Typography>
                <Typography component="p" className="menu-library-share-store-target-dialog__store-card-meta">
                    {dateLabel}
                </Typography>
                <Typography component="p" className="menu-library-share-store-target-dialog__store-card-action">
                    {i18n.t('menu_library_share_target_update_label')}
                </Typography>
            </Box>
        </StoreLikeCardShell>
    );
}

type NewMenuCardProps = {
    /** 是否选中 */
    selected: boolean;
    /** 选中回调 */
    onSelect: () => void;
};

/**
 * 「新增菜单」卡片：Figma Frame 1215 — 168×224，无封面图，上 68px 后 40×40「+」，再 24px 间距后文案
 */
function NewMenuCard(props: NewMenuCardProps) {
    const { selected, onSelect } = props;

    const handleKeyDown = (ev: React.KeyboardEvent) => {
        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            onSelect();
        }
    };

    return (
        <Box
            role="button"
            tabIndex={0}
            className={[
                'menu-library-share-store-target-dialog__new-card',
                selected ? 'menu-library-share-store-target-dialog__new-card--selected' : '',
            ]
                .filter(Boolean)
                .join(' ')}
            onClick={onSelect}
            onKeyDown={handleKeyDown}
        >
            <AddIcon className="menu-library-share-store-target-dialog__new-card-add" aria-hidden />
            <Typography component="p" className="menu-library-share-store-target-dialog__new-card-label">
                {i18n.t('menu_library_share_target_new_card')}
            </Typography>
        </Box>
    );
}

/**
 * 分享至菜单商店 — Figma 4029:27608；壳层使用公共 `DialogComponent`
 */
export function MenuLibraryShareStoreTargetDialog(props: MenuLibraryShareStoreTargetDialogProps) {
    const {
        open,
        onClose,
        menuName,
        loading,
        submitting,
        uploadedMenus,
        loadingMore,
        onLoadMore,
        selection,
        onSelectionChange,
        onSubmit,
    } = props;

    /** 与 `VirtuosoGrid` 同源数据：首格新增 + 已上传列表，同一 List 内 flex 换行 */
    const shareStoreGridRows = useMemo((): readonly ShareStoreTargetGridRow[] => {
        const rows: ShareStoreTargetGridRow[] = [{ kind: 'new' }];
        for (const item of uploadedMenus) {
            rows.push({ kind: 'menu', item });
        }
        return rows;
    }, [uploadedMenus]);

    /** 虚拟栅格单元：首格 `NewMenuCard`，其余 `ExistingMenuCard` */
    const renderShareStoreGridCell = useCallback(
        (_index: number, row: ShareStoreTargetGridRow) => {
            if (row.kind === 'new') {
                return <NewMenuCard selected={selection === 'new'} onSelect={() => onSelectionChange('new')} />;
            }
            return (
                <ExistingMenuCard
                    item={row.item}
                    selected={selection === row.item._id}
                    onSelect={() => onSelectionChange(row.item._id)}
                />
            );
        },
        [selection, onSelectionChange],
    );

    /** 提交中不可关闭（遮罩 / Esc）；签名兼容 MUI `Dialog`，不使用事件参数 */
    const handleDialogClose = useCallback(() => {
        if (submitting) {
            return;
        }
        onClose();
    }, [onClose, submitting]);

    const subtitleText = menuName.trim() || '—';
    const submitKey =
        selection === 'new' ? 'menu_library_share_target_submit_new' : 'menu_library_share_target_submit_overwrite';

    return (
        <DialogComponent
            className="menu-library-share-store-target-dialog"
            open={open}
            onClose={handleDialogClose}
            maxWidth={false}
            fullWidth
            aria-labelledby="menu-library-share-store-target-title"
            PaperProps={{
                className: 'menu-library-share-store-target-dialog__paper',
                elevation: 0,
                sx: {
                    width: '100%',
                    /** 745 为稿面宽；略增以容纳 697 宽栅格 + 纵向滚动条占位（否则只能排 3 列） */
                    maxWidth: '758px',
                    boxSizing: 'border-box',
                    background: 'var(--fill-neutral-100)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    border: '2px solid var(--fill-primary-300)',
                    boxShadow: '0 0 8px 0 var(--shadow-50)',
                },
            }}
            sx={[
                {
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'var(--overlay-800-a25)',
                    },
                    '& .MuiDialog-paper': {
                        marginTop: '-32px',
                    },
                    '& .MuiDialog-container > .MuiPaper-root': {
                        background: 'var(--fill-neutral-100)',
                        backgroundColor: 'var(--fill-neutral-100)',
                        border: '2px solid var(--fill-primary-300)',
                        boxShadow: '0 0 8px 0 var(--shadow-50)',
                    },
                    '& .MuiDialogTitle-root': {
                        backgroundColor: 'var(--fill-neutral-100)',
                        paddingTop: '16px',
                        /** 与正文左右 20px 对齐，避免标题区比列表更「窄」 */
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        paddingBottom: '0px',
                        width: '100%',
                        boxSizing: 'border-box',
                    },
                    '& .MuiDialogContent-root': {
                        paddingTop: '24px !important',
                        /** 仅列表区内滚动，避免整段 `DialogContent` 出现外层滚动条 */
                        overflow: 'hidden',
                    },
                    '& .dialog-footer': {
                        display: 'none',
                    },
                },
            ]}
            dialogContentStyle={{
                paddingTop: '24px',
                /** 与稿 697px 栅格对齐：`(758 - 4 边框 - 40 左右内边距) - 滚动条 ≈ 697`，保证一行 4×168 + 3×gap */
                paddingLeft: '20px',
                paddingRight: '20px',
                paddingBottom: '24px',
            }}
            defaultHeaderProps={{
                titleAlign: 'left',
                isHiddenCloseIcon: true,
                onClose: () => {
                    if (!submitting) {
                        onClose();
                    }
                },
                titleContent: (
                    <Box
                        className="menu-library-share-store-target-dialog__title-row"
                        sx={{ width: '100%', boxSizing: 'border-box' }}
                    >
                        <Box className="menu-library-share-store-target-dialog__title-block">
                            <Typography
                                id="menu-library-share-store-target-title"
                                component="h2"
                                className="menu-library-share-store-target-dialog__title"
                            >
                                {i18n.t('menu_library_detail_share_menu')}
                            </Typography>
                            <Typography component="p" className="menu-library-share-store-target-dialog__subtitle">
                                {subtitleText}
                            </Typography>
                        </Box>
                        <IconButton
                            type="button"
                            size="small"
                            onClick={() => !submitting && onClose()}
                            disabled={submitting}
                            aria-label={i18n.t('extra_cost_text_close')}
                            className="menu-library-share-store-target-dialog__close"
                        >
                            <CloseIcon width={24} height={24} aria-hidden />
                        </IconButton>
                    </Box>
                ),
            }}
        >
            <Box className="menu-library-share-store-target-dialog__content">
                <Stack spacing={6} className="menu-library-share-store-target-dialog__stack">
                    {/** 菜单列表区：高度上限内纵向滚动，底部说明与主按钮保持固定不随列表滚动 */}
                    <Box
                        className={[
                            'menu-library-share-store-target-dialog__list-scroll',
                            !loading ? 'menu-library-share-store-target-dialog__list-scroll--virtuoso' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                    >
                        {loading ? (
                            <Box className="menu-library-share-store-target-dialog__loading">
                                <CircularProgress size={40} aria-label={i18n.t('general_text_loading')} />
                            </Box>
                        ) : (
                            <MenuStoreVirtuosoPagedGrid<ShareStoreTargetGridRow>
                                items={shareStoreGridRows}
                                loadingMore={loadingMore}
                                onLoadMore={onLoadMore}
                                listResetKey="share-store-target-uploaded"
                                computeItemKey={(_index, row) => (row.kind === 'new' ? 'share-store-new' : row.item._id)}
                                itemContent={renderShareStoreGridCell}
                                listClassName="menu-library-share-store-target-dialog__virtuoso-list"
                                itemClassName="menu-library-share-store-target-dialog__virtuoso-cell"
                                shellClassName="menu-library-share-store-target-dialog__virtuoso-shell"
                                gridRootClassName="menu-library-share-store-target-dialog__virtuoso-root"
                                scrollerClassName="menu-library-share-store-target-dialog__virtuoso-scroller"
                                footerClassName="menu-library-share-store-target-dialog__virtuoso-footer"
                                increaseViewportBottom={224 * 4}
                                increaseViewportTop={224 * 3}
                                overscanMain={280}
                            />
                        )}
                    </Box>

                    <Typography variant="body2" color="text.secondary" className="menu-library-share-store-target-dialog__hint">
                        {i18n.t('menu_library_share_target_footer_hint')}
                    </Typography>

                    <LoadingOverlayButton
                        type="button"
                        variant="contained"
                        disableElevation
                        fullWidth
                        className="menu-library-share-store-target-dialog__submit"
                        disabled={loading || submitting}
                        loading={submitting}
                        loadingIndicatorSize={22}
                        onClick={() => onSubmit()}
                        startIcon={
                            <MenuBookRoundedIcon className="menu-library-share-store-target-dialog__submit-icon" aria-hidden />
                        }
                    >
                        {i18n.t(submitKey)}
                    </LoadingOverlayButton>
                </Stack>
            </Box>
        </DialogComponent>
    );
}

export default MenuLibraryShareStoreTargetDialog;
